import axios from 'axios'; // we'll use this to make post/get requests
import {getUserToken} from '../helpers/localStorage';
import {Base, Report} from './Urls';

/*
 * Returns an object with the data from the response.  If unsuccessful will return object containing an error variable
 * ie. { error: "Invalid username or password!"}
 * @param {route to post to} route
 * @param {json data to send} data
 * @param {default true, boolean value whether or not the post needs authorization} authorize
 */
async function post(route, data, authorize = true) {
  const url = apiBase() + route;
  const token = getUserToken();
  const authHeader = 'Bearer ' + token;
  const header = authorize
    ? {
        Authorization: authHeader,
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      }
    : {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      };

  // If the api endpoint only takes a "string" wrap the data in quotes before sending it to this function
  const options = {
    method: 'POST',
    url: url,
    headers: header,
    data: data,
  };

  return await sendAxios(options);
}

/*
 * Returns an object with the data from the response.  If unsuccessful will return object containing an error variable
 * ie. { error: "Invalid username or password!"}
 * @param {route to post to} route
 * @param {file data to send} data
 * @param {default true, boolean value whether or not the post needs authorization} authorize
 */
async function postUpload(route, data, authorize = true) {
  const url = apiBase() + route;
  const token = getUserToken();
  const authHeader = 'Bearer ' + token;
  const header = authorize
    ? {
        Authorization: authHeader,
        Accept: 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
      }
    : {
        Accept: 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
      };
  // If the api endpoint only takes a "string" wrap the data in quotes before sending it to this function
  const options = {
    method: 'POST',
    url: url,
    headers: header,
    data: data,
  };

  return await sendAxios(options);
}

/*
 *  Same as the post function but doesn't require authorization token
 * @param {route to post to} route
 * @param {Json data to send} data
 * @returns
 */
async function postNoAuth(route, data) {
  // do a normal post, just pass in false for auth parameter
  return await post(route, data, false);
}

async function get(route, authorize = true, isFile = false, fileType = 'pdf') {
  const url = apiBase() + route;
  const token = getUserToken();
  const authHeader = 'Bearer ' + token;
  const header = authorize ? {Authorization: authHeader} : {};
  if (isFile) {
    // Map file types to correct MIME types
    const mimeTypes = {
      pdf: 'application/pdf',
      excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      csv: 'text/csv',
    };
    const mimeType = mimeTypes[fileType] || `application/${fileType}`;
    // Only set Accept header for GET requests, not Content-Type
    header['Accept'] = mimeType;
  }
  const options = {
    method: 'GET',
    url: url,
    headers: header,
  };
  if (isFile) {
    options.responseType = 'blob';
  }

  return await sendAxios(options);
}

async function getNoAuth(route) {
  return await get(route, false);
}

// delete is a reserved word in javascript apparently, couldn't use it as name
async function postDelete(route, data, authorize = true) {
  const url = apiBase() + route;
  const token = getUserToken();
  const authHeader = 'Bearer ' + token;
  const header = authorize
    ? {
        Authorization: authHeader,
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      }
    : {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      };
  const options = {
    method: 'DELETE',
    url: url,
    headers: header,
    data: data,
  };

  return await sendAxios(options);
}

function apiBase() {
  const env = process.env.REACT_APP_ENV.trim();
  if (env === 'development') {
    return Base.devApi;
  } else if (env === 'staging') {
    return Base.stagingApi;
  } else if (env === 'production') {
    return Base.prodApi;
  }
}

/*
 * Hopefully covering all the bases here, usually errors > 400 are caught from my testing, but added extra logic just in case.
 * Since Get and Post should always have isSuccess and errorMessage we can share this error handling logic.
 * @param {axios options} options
 * @returns response or error object
 */
async function sendAxios(options) {
  let retVal = '';
  try {
    const response = await axios(options);
    const responseOK = response && response.status === 200;
    let error = '';
    if (responseOK) {
      const responseData = await response.data;
      if (options.method === 'GET' || responseData?.isSuccess || (Array.isArray(responseData) && responseData[0]?.isSuccess)) {
        retVal = responseData;
      } else {
        error = responseData?.errorMessage ? responseData.errorMessage : 'Request Was Unsuccessful';
        retVal = {error: error};
      }
    } else {
      const responseData = await response.data;
      error = responseData?.errorMessage ? responseData.errorMessage : 'Error: Bad Request';
      retVal = {error: error};
    }
  } catch (err) {
    let error = '';
    const responseData = await err.response;
    if (responseData?.data?.errorMessage) {
      error = responseData.data.errorMessage;
    } else if (Array.isArray(responseData?.data) && responseData?.data[0]?.errorMessage) {
      error = responseData.data[0].errorMessage;
    } else if (responseData?.data?.errors) {
      const key = Object.keys(responseData.data.errors)[0];
      if (key) {
        error = responseData.data.errors[key];
      } else {
        error = err.message;
      }
    } else {
      let statusText = err?.response?.statusText;
      statusText = statusText ? statusText : '';
      error = err.message + ' ' + statusText;
    }
    retVal = {error: error};
  }
  return retVal;
}

/*
 * Returns a url with the pdf contents, go to this url for treasure
 * Just sending in the empty strings or default values would cause some type errors with the API.
 * So in this case if a value is null(ie. not touched by the user) we won't send it at all.
 * @param {*} data for report
 */
async function getReport(data) {
  const reportObj = {
    ArrangerId: data.arrangerId ?? null,
    AtPreneedBoth: data.atPreneedBoth ?? null,
    Big9Chart: data.big9Chart ?? null,
    ClientCode: data.clientCode ?? null,
    ClientId: data.clientId ?? null,
    Contract: data.contract ?? null,
    CommentsNoCommentsAll: data.commentsNoCommentsAll ?? null,
    EmDept: data.emDept ?? null,
    EndBatch: data.endBatch ?? null,
    EndDate: data.endDate ?? null,
    EndDate2: data.endDate2 ?? null,
    EndTime: data.endTime ?? null,
    FormCode: data.formCode ?? null,
    GroupId: data.groupId ?? null,
    IncludeEmail: data.includeEmail ?? null,
    IncludeInactiveArrangers: data.includeInactiveArrangers ?? null,
    IsBatchSumEmailOnly: data.isBatchSumEmailOnly ?? null,
    IsCommentReportByLocation: data.isCommentReportByLocation ?? null,
    IsContractDate: data.isContractDate ?? null,
    IsElectronic: data.isElectronic ?? null,
    IsEmailOnly: data.isEmailOnly ?? null,
    IsExcludeFromSum: data.isExcludeFromSum ?? null,
    IsImportDate: data.isImportDate ?? null,
    IsLocation: data.isLocation ?? null,
    IsNoneReceived: data.isNoneReceived ?? null,
    IsOpenBatchesOnly: data.isOpenBatchesOnly ?? null,
    IsSecondaryArranger: data.isSecondaryArranger ?? null,
    IsTPI: data.isTPI ?? null,
    ImpAnalysisContractOrImport: data.impAnalysisContractOrImport ?? null,
    LocationId: data.locationId ?? null,
    MinNumSurveys: data.minNumSurveys ?? null,
    NumMos4Dashboard: data.numMos4Dashboard ?? null,
    PreneedRevFunCremBoth: data.preneedRevFunCremBoth ?? null,
    PromptId: data.promptId ?? null,
    IsPrecoa: data.precoa ?? null,
    QuestionSelected: data.questionSelected ?? null,
    RegionId: data.regionId ?? null,
    ReportFilterType: data.reportFilterType ?? null,
    ScanForPhrase: data.scanForPhrase ?? null,
    SectionId: data.sectionId ?? null,
    ServiceType: data.serviceType ?? null,
    StartBatch: data.startBatch ?? null,
    StartDate: data.startDate ?? null,
    StartDate2: data.startDate2 ?? null,
    StartTime: data.startTime ?? null,
    ValueSatisfactionBoth: data.valueSatisfactionBoth ?? null,
  };
  Object.keys(reportObj).forEach(k => (reportObj[k] === null || reportObj[k] === '') && delete reportObj[k]);
  const url = Report.REPORT_FILES + `/` + reportObj.FormCode;
  const requestUrl = queryStringBuilder(url, reportObj);
  const pdf = await getFile(requestUrl);
  if (pdf.error) {
    return pdf;
  } else {
    // const pdf = await getFile(
    //   '/Report/report-files/7cf26ae0-e52e-4544-b558-1450e324dc0e?PromptId=mNnQOAL7djrP&SectionId=Yb4LOe479jvP&IsSecondaryArranger=true&ServiceType=1&StartDate=2021-01-01&EndDate=2021-12-31&ClientCode=1058'
    // );
    const fileURL = window.URL.createObjectURL(pdf);
    return fileURL;
  }
}

// This is silly, but if we are expecting a file we can't get the string error message back from api
// so if we error out initially, resend the response but expect a normal response to get the actual error message
// otherwise return the initial file that we get.
async function getFile(route, fileType = 'pdf') {
  const initialGet = await get(route, true, true, fileType);
  if (initialGet.error) return await get(route);
  else return initialGet;
}

function queryStringBuilder(route, params) {
  const paramList = new URLSearchParams(params);
  return route + '?' + paramList.toString();
}

export {post, get, getFile, getReport, postNoAuth, getNoAuth, postDelete, postUpload, queryStringBuilder};
